import { writable, derived, get } from 'svelte/store';
import { fetchSelection } from '$lib/api/selection';
import { scoreFilterRange, queryResult } from './selection';
import type { CompanyWithContacts, Contact } from '$lib/types/api';

interface CompanyMetadata {
  source: 'search' | 'similar';
  addedFrom?: string; // ID of company it was found similar to
  similarityScore?: number;
}

interface SimilarityBatch {
  sourceCompanyId: string;
  sourceCompanyName: string;
  companyIds: string[]; // Currently visible company IDs after filtering
  originalCompanyIds: string[]; // All company IDs originally added in this batch
  contactIds: string[]; // Currently visible contact IDs after filtering
  originalContactIds: string[]; // All contact IDs originally added in this batch
  addedAt: Date;
}

interface MixedResultsState {
  /** All companies, from the search and from similarity together. */
  companies: CompanyWithContacts[];
  metadata: Record<string, CompanyMetadata>; // Track source of each company
  similarityBatches: SimilarityBatch[]; // Track batches of similar companies
  loading: boolean;
}

// Main store for mixed results
export const mixedResults = writable<MixedResultsState>({
  companies: [],
  metadata: {},
  similarityBatches: [],
  loading: false
});

// Store original search results when they come in
export function setOriginalResults(companies: CompanyWithContacts[]) {
  const metadata: Record<string, CompanyMetadata> = {};
  companies.forEach((company) => {
    metadata[company.id] = { source: 'search' };
  });

  mixedResults.set({
    companies,
    metadata,
    similarityBatches: [], // Clear similarity batches on new search
    loading: false
  });
}

// Update results from filtering while preserving similarity metadata
export function updateFilteredResults(companies: CompanyWithContacts[]) {
  const state = get(mixedResults);
  const newMetadata: Record<string, CompanyMetadata> = {};

  // Create a map of original companies by ID to preserve their scores
  const originalCompaniesById = new Map<string, CompanyWithContacts>();
  state.companies.forEach((c) => {
    originalCompaniesById.set(c.id, c);
  });

  // Rebuild metadata for filtered companies and preserve scores
  const companiesWithScores = companies.map((company) => {
    const originalCompany = originalCompaniesById.get(company.id);

    // Check if this company was originally from similarity search
    let fromSimilarity = false;
    let sourceId = '';

    for (const batch of state.similarityBatches) {
      if (batch.originalCompanyIds.includes(company.id)) {
        fromSimilarity = true;
        sourceId = batch.sourceCompanyId;
        break;
      }
    }

    if (fromSimilarity) {
      newMetadata[company.id] = {
        source: 'similar',
        addedFrom: sourceId,
        similarityScore: originalCompany?.quality || company.quality
      };
    } else {
      newMetadata[company.id] = { source: 'search' };
    }

    // Preserve the original quality if the filtered row lost it. A parallel
    // `score` stood beside it; the API reports similarity as `quality` and has
    // never sent `score`.
    return {
      ...company,
      quality: company.quality || originalCompany?.quality
    };
  });

  // Update similarity batches to reflect current visible IDs
  const companyIds = new Set(companies.map((c) => c.id));

  // Build a map of visible contact IDs per batch
  const contactIdsByBatch = new Map<string, Set<string>>();
  companies.forEach((company) => {
    // Find which batch this company belongs to
    for (const batch of state.similarityBatches) {
      if (batch.originalCompanyIds.includes(company.id)) {
        if (!contactIdsByBatch.has(batch.sourceCompanyId)) {
          contactIdsByBatch.set(batch.sourceCompanyId, new Set());
        }
        // Add all contact IDs from this company
        if (company.contacts && Array.isArray(company.contacts)) {
          company.contacts.forEach((contact: Contact) => {
            if (contact.id) {
              contactIdsByBatch.get(batch.sourceCompanyId)!.add(contact.id);
            }
          });
        }
        break;
      }
    }
  });

  const similarityBatches = state.similarityBatches.map((batch) => ({
    ...batch,
    companyIds: batch.originalCompanyIds.filter((id) => companyIds.has(id)),
    contactIds: Array.from(contactIdsByBatch.get(batch.sourceCompanyId) || [])
  }));

  mixedResults.set({
    companies: companiesWithScores,
    metadata: newMetadata,
    similarityBatches,
    loading: false
  });
}

// Add companies from similarity search
export function addSimilarCompanies(
  newCompanies: CompanyWithContacts[],
  sourceCompanyId: string,
  sourceCompanyName: string
) {
  mixedResults.update((state) => {
    const existingIds = new Set(state.companies.map((c) => c.id));
    const metadata = { ...state.metadata };
    const companies = [...state.companies];
    const newCompanyIds: string[] = [];
    const newContactIds: string[] = [];

    // Add only companies that aren't already in the list
    newCompanies.forEach((company) => {
      if (!existingIds.has(company.id)) {
        // Ensure company has contacts array
        const companyWithContacts = {
          ...company,
          contacts: company.contacts || []
        };
        companies.push(companyWithContacts);
        newCompanyIds.push(company.id);

        // Collect contact IDs from this company
        if (companyWithContacts.contacts && Array.isArray(companyWithContacts.contacts)) {
          companyWithContacts.contacts.forEach((contact: Contact) => {
            if (contact.id) {
              newContactIds.push(contact.id);
            }
          });
        }

        metadata[company.id] = {
          source: 'similar',
          addedFrom: sourceCompanyId,
          similarityScore: company.quality
        };
      }
    });

    // Update or create batch for this source company
    const similarityBatches = [...state.similarityBatches];
    if (newCompanyIds.length > 0) {
      // Check if we already have a batch for this source company
      const existingBatchIndex = similarityBatches.findIndex(
        (b) => b.sourceCompanyId === sourceCompanyId
      );

      if (existingBatchIndex >= 0) {
        // Update existing batch
        const existingBatch = similarityBatches[existingBatchIndex];
        similarityBatches[existingBatchIndex] = {
          ...existingBatch,
          companyIds: [...existingBatch.companyIds, ...newCompanyIds],
          originalCompanyIds: [...existingBatch.originalCompanyIds, ...newCompanyIds],
          contactIds: [...(existingBatch.contactIds || []), ...newContactIds],
          originalContactIds: [...(existingBatch.originalContactIds || []), ...newContactIds],
          addedAt: new Date() // Update timestamp
        };
      } else {
        // Create new batch
        similarityBatches.push({
          sourceCompanyId,
          sourceCompanyName,
          companyIds: newCompanyIds,
          originalCompanyIds: newCompanyIds,
          contactIds: newContactIds,
          originalContactIds: newContactIds,
          addedAt: new Date()
        });
      }
    }

    return { ...state, companies, metadata, similarityBatches };
  });
}

// Remove a company from results
export function removeCompany(companyId: string) {
  mixedResults.update((state) => {
    const companies = state.companies.filter((c) => c.id !== companyId);
    const metadata = { ...state.metadata };
    delete metadata[companyId];

    return { ...state, companies, metadata };
  });
}

// Remove a similarity batch
export function removeSimilarityBatch(sourceCompanyId: string) {
  mixedResults.update((state) => {
    // Find the batch to remove
    const batchToRemove = state.similarityBatches.find(
      (b) => b.sourceCompanyId === sourceCompanyId
    );
    if (!batchToRemove) {
      return state;
    }

    // Create a set of company IDs to remove - only those that are actually marked as similar
    const companyIdsToRemove = new Set<string>();
    batchToRemove.originalCompanyIds.forEach((id) => {
      // Only add to removal set if this company is marked as similar AND from this batch
      if (
        state.metadata[id]?.source === 'similar' &&
        state.metadata[id]?.addedFrom === sourceCompanyId
      ) {
        companyIdsToRemove.add(id);
      }
    });

    // If no companies to remove, just remove the batch
    if (companyIdsToRemove.size === 0) {
      return {
        ...state,
        similarityBatches: state.similarityBatches.filter(
          (b) => b.sourceCompanyId !== sourceCompanyId
        )
      };
    }

    // Create a set of contact IDs to remove from the batch
    const contactIdsToRemove = new Set<string>(batchToRemove.originalContactIds || []);

    // Filter out companies and remove their contacts
    const companies = state.companies
      .filter((company) => {
        // Remove companies that are in the removal set
        if (companyIdsToRemove.has(company.id)) {
          return false;
        }
        return true;
      })
      .map((company) => {
        // If this company has contacts from the removed batch, filter them out
        if (company.contacts && company.contacts.length > 0 && contactIdsToRemove.size > 0) {
          const filteredContacts = company.contacts.filter(
            (contact: Contact) => !contactIdsToRemove.has(contact.id)
          );

          // Only return modified company if contacts were actually removed
          if (filteredContacts.length !== company.contacts.length) {
            return { ...company, contacts: filteredContacts };
          }
        }

        return company;
      });

    // Remove metadata for these companies
    const metadata = { ...state.metadata };
    companyIdsToRemove.forEach((id) => {
      delete metadata[id];
    });

    // Remove the batch
    const similarityBatches = state.similarityBatches.filter(
      (b) => b.sourceCompanyId !== sourceCompanyId
    );

    // If we have no more similarity batches but still have companies,
    // ensure they're all marked as 'search' results
    if (similarityBatches.length === 0 && companies.length > 0) {
      companies.forEach((company) => {
        if (!metadata[company.id]) {
          metadata[company.id] = { source: 'search' };
        }
      });
    }

    // Check if we need to restore search results
    // This can happen if all companies were from similarity and we removed them all
    if (companies.length === 0) {
      const currentQueryResult = get(queryResult);

      // Check if we have search results that should be restored
      const hasSearchResults = currentQueryResult && currentQueryResult.length > 0;
      const onlyHadSimilarityResults = Object.values(state.metadata).every(
        (m) => m.source === 'similar'
      );

      if (hasSearchResults && onlyHadSimilarityResults) {
        // Restore original search results
        const restoredMetadata: Record<string, CompanyMetadata> = {};
        const restoredCompanies: CompanyWithContacts[] = [];

        currentQueryResult.forEach((company: CompanyWithContacts) => {
          // Only restore if not already in a remaining similarity batch
          let isInRemainingSimilarityBatch = false;
          for (const batch of similarityBatches) {
            if (batch.originalCompanyIds.includes(company.id)) {
              isInRemainingSimilarityBatch = true;
              break;
            }
          }

          if (!isInRemainingSimilarityBatch) {
            restoredCompanies.push(company);
            restoredMetadata[company.id] = { source: 'search' };
          }
        });

        if (restoredCompanies.length > 0) {
          return {
            ...state,
            companies: restoredCompanies,
            metadata: restoredMetadata,
            similarityBatches
          };
        }
      }
    }

    // Final state
    return { ...state, companies, metadata, similarityBatches };
  });

  // Force a re-evaluation of queryResult -> mixedResults sync
  // This ensures the Results component re-syncs if needed
  const currentQueryResult = get(queryResult);
  const currentMixedResults = get(mixedResults);

  if (
    currentMixedResults.companies.length === 0 &&
    currentMixedResults.similarityBatches.length === 0 &&
    currentQueryResult &&
    currentQueryResult.length > 0
  ) {
    setOriginalResults(currentQueryResult);
  }
}

// Get all company IDs including original similarity IDs
export function getAllCompanyIds(): string[] {
  const state = get(mixedResults);
  const allIds = new Set<string>();

  // Add all current companies
  state.companies.forEach((c) => allIds.add(c.id));

  // Add all original similarity IDs (even if filtered out)
  state.similarityBatches.forEach((batch) => {
    batch.originalCompanyIds.forEach((id) => allIds.add(id));
  });

  return Array.from(allIds);
}

// Apply filters to current mixed results
export async function applyFiltersToMixed() {
  const state = get(mixedResults);
  if (state.companies.length === 0 && state.similarityBatches.length === 0) return;

  mixedResults.update((s) => ({ ...s, loading: true }));

  try {
    // Narrow within every identifier held, including the lookalike ones.
    //
    // Naming them is the only way: those companies came from a vector search
    // around one company and cannot be described by the filters, so a re-run
    // that searched afresh would drop them.
    //
    // The answer is an index, not rows — the same two-part fetch a search
    // uses. Rows already held are kept below, so nothing that was on screen
    // becomes a skeleton again just because the filters changed.
    const companyIds = getAllCompanyIds();
    const selection = await fetchSelection(companyIds);

    const held = new Map(state.companies.map((c) => [c.id, c]));

    const filteredCompanies: CompanyWithContacts[] = selection.companies.map((company) => {
      const before = held.get(company.id);

      // The index is authoritative about who is in the selection and which
      // people come with them; the row already fetched is authoritative about
      // what to display. Keeping the latter is what stops a filter change
      // blanking the visible list.
      if (before?.hydrated) {
        return { ...before, contacts: company.contacts, quality: company.quality };
      }
      return company;
    });

    const filteredIds = new Set(filteredCompanies.map((c) => c.id));

    // Create a map of original companies by ID to preserve their scores
    const originalCompaniesById = new Map<string, CompanyWithContacts>();
    state.companies.forEach((c) => {
      originalCompaniesById.set(c.id, c);
    });

    // Rebuild metadata for filtered companies, preserving similarity info
    const newMetadata: Record<string, CompanyMetadata> = {};

    // Process companies and preserve scores
    const companiesWithScores = filteredCompanies.map((company) => {
      const originalCompany = originalCompaniesById.get(company.id);

      // Check if this company was originally from similarity search
      let fromSimilarity = false;
      let sourceId = '';

      for (const batch of state.similarityBatches) {
        if (batch.originalCompanyIds.includes(company.id)) {
          fromSimilarity = true;
          sourceId = batch.sourceCompanyId;
          break;
        }
      }

      if (fromSimilarity) {
        newMetadata[company.id] = {
          source: 'similar',
          addedFrom: sourceId,
          similarityScore: originalCompany?.quality || company.quality
        };
      } else {
        newMetadata[company.id] = { source: 'search' };
      }

      // Preserve the original quality if the filtered row lost it. There was
      // a parallel `score` here; the API reports similarity as `quality` and
      // has never sent `score`.
      return {
        ...company,
        quality: company.quality || originalCompany?.quality
      };
    });

    // Build a map of visible contact IDs per batch.
    const contactIdsByBatch = new Map<string, Set<string>>();
    filteredCompanies.forEach((company) => {
      // Find which batch this company belongs to
      for (const batch of state.similarityBatches) {
        if (batch.originalCompanyIds.includes(company.id)) {
          if (!contactIdsByBatch.has(batch.sourceCompanyId)) {
            contactIdsByBatch.set(batch.sourceCompanyId, new Set());
          }
          // Add all contact IDs from this company
          if (company.contacts && Array.isArray(company.contacts)) {
            company.contacts.forEach((contact: Contact) => {
              if (contact.id) {
                contactIdsByBatch.get(batch.sourceCompanyId)!.add(contact.id);
              }
            });
          }
          break;
        }
      }
    });

    // Update similarity batches to reflect current visible IDs
    const similarityBatches = state.similarityBatches.map((batch) => ({
      ...batch,
      companyIds: batch.originalCompanyIds.filter((id) => filteredIds.has(id)),
      contactIds: Array.from(contactIdsByBatch.get(batch.sourceCompanyId) || [])
    }));

    mixedResults.set({
      companies: companiesWithScores,
      metadata: newMetadata,
      similarityBatches,
      loading: false
    });
  } catch {
    mixedResults.update((s) => ({ ...s, loading: false }));
  }
}

// Derived store for filtered results including score filter
export const filteredMixedResults = derived(
  [mixedResults, scoreFilterRange],
  ([$mixedResults, $scoreFilterRange]) => {
    const [minScore, maxScore] = $scoreFilterRange;

    if (minScore === 0 && maxScore === 100) {
      return $mixedResults.companies;
    }

    return $mixedResults.companies.filter((company) => {
      const score = company.quality || 0;
      const scorePercentage = score * 100;
      return scorePercentage >= minScore && scorePercentage <= maxScore;
    });
  }
);

// Helper to check if we have mixed sources
export const hasMixedSources = derived(mixedResults, ($mixedResults) => {
  const sources = new Set(Object.values($mixedResults.metadata).map((m) => m.source));
  return sources.size > 1;
});
